/**
 * ---------------------------------------------------------------------------------------------------------------------
 * STICKY ADD TO CART
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Comportamiento de la barra fija de compra ('snippets/sticky-add-to-cart.liquid'). Hace tres cosas:
 *
 *   1. Mostrarla cuando el bloque de compra de la ficha deja de verse (y ocultarla de nuevo al llegar al pie).
 *   2. Mantener su selector de variante sincronizado con el de la ficha, en los dos sentidos.
 *   3. Llevar la hoja de opciones de móvil: adelantar su contenido al abrirse y cerrarla una vez añadido el producto.
 *
 * Sobre el punto 2: la barra vive en el "Overlay group" y no dentro de la sección del producto, así que no puede
 * compartir formulario con la ficha (el re-render de variante del tema sustituye HTML por ID dentro de la sección que
 * se vuelve a pedir, y la barra no está en ella). Cada uno tiene su formulario y aquí se replica la selección de uno
 * en el otro.
 *
 * El bucle de ecos se corta solo: tras sincronizar, ambos formularios tienen las mismas opciones activas, y la
 * comparación previa hace que el "variant:change" de vuelta no dispare nada.
 *
 * Mientras la hoja de móvil está abierta esa sincronización se aplaza hasta que se cierre: la ficha queda tapada, y
 * rehacerla —que supone volver a pedir la sección entera del producto— solo le quitaría ancho de banda a lo que sí se
 * está mirando.
 */

const CHECKED_OPTION_SELECTOR = 'input[data-option-position]:checked';

/**
 * Devuelve los IDs de valor de opción activos en un formulario, ordenados por posición de opción. Es el formato que
 * espera VariantPicker.selectCombination() y, al ser IDs de valor de opción, son los mismos en los dos formularios.
 */
function activeOptionValues(form) {
  return Array.from(form.elements)
    .filter((element) => element.matches?.(CHECKED_OPTION_SELECTOR))
    .sort(
      (first, second) =>
        parseInt(first.getAttribute('data-option-position'), 10) -
        parseInt(second.getAttribute('data-option-position'), 10)
    )
    .map((element) => element.value);
}

class StickyAddToCart extends HTMLElement {
  #abortController;
  #intersectionObserver;
  #triggerElement;
  #footerElement;
  #triggerIsPassed = false;
  #footerIsVisible = false;
  #syncIsPending = false;
  #optionsArePrerendered = false;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#intersectionObserver = new IntersectionObserver(this.#onIntersection.bind(this));

    this.#observeTrigger();

    // "toggle" es el único evento del diálogo que burbujea, así que se escucha desde el documento: los demás obligarían
    // a enganchar el oyente en la hoja, que todavía no existe cuando la barra se conecta (el navegador aún no ha leído
    // sus hijos).
    document.addEventListener('toggle', this.#onSheetToggle.bind(this), {
      signal: this.#abortController.signal,
    });

    // El evento burbujea desde el formulario que cambió, así que se escucha una sola vez en el documento.
    document.addEventListener('variant:change', this.#onVariantChange.bind(this), {
      signal: this.#abortController.signal,
    });

    // Al añadir, el tema abre solo el carrito lateral. Si la hoja siguiera arriba quedarían dos diálogos apilados y el
    // scroll bloqueado por partida doble, así que se cierra — es además lo que se espera después de comprar. Con el
    // carrito en modo "mensaje" no se toca: ahí el aviso de "añadido" sale dentro de la propia hoja.
    if (window.themeVariables?.settings?.cartType === 'drawer') {
      document.addEventListener('variant:add', this.#onVariantAdd.bind(this), {
        signal: this.#abortController.signal,
      });
    }
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#abortController.abort();
  }

  get #form() {
    return document.forms[this.getAttribute('form')];
  }

  get #variantPicker() {
    return this.querySelector('variant-picker');
  }

  /**
   * La hoja de opciones de móvil. Se busca por ID en el documento y no dentro de la barra porque, mientras está
   * abierta, el diálogo vive colgado del <body>.
   */
  get #sheet() {
    return document.getElementById(this.getAttribute('sheet'));
  }

  /**
   * El selector de la ficha es el único que lleva "update-url" (solo lo pone el contexto "main_product"), así que sirve
   * para distinguirlo del de la barra o del de un modal de compra rápida. Se busca en cada uso y no se cachea porque el
   * re-render de variante sustituye el elemento.
   */
  get #mainVariantPicker() {
    const handle = this.#variantPicker?.getAttribute('handle');

    return handle
      ? document.querySelector(`variant-picker[update-url][handle="${CSS.escape(handle)}"]`)
      : null;
  }

  /**
   * Se observa el formulario de compra de la ficha (y no el botón): el re-render parcial del tema sí reemplaza
   * <buy-buttons>, con lo que el observador se quedaría apuntando a un nodo desconectado.
   */
  #observeTrigger() {
    const customSelector = this.getAttribute('trigger');
    const mainSection = document.querySelector('.shopify-section--main-product');

    this.#triggerElement = customSelector
      ? document.querySelector(customSelector)
      : mainSection?.querySelector('form[action*="/cart/add"]') ?? mainSection;

    // Sin punto de referencia la barra no puede saber cuándo aparecer: mejor no dejarla fija en pantalla.
    if (!this.#triggerElement) {
      this.hidden = true;
      return;
    }

    this.#intersectionObserver.observe(this.#triggerElement);

    if (this.hasAttribute('hide-over-footer')) {
      this.#footerElement = document.querySelector('.shopify-section--footer');

      if (this.#footerElement) {
        this.#intersectionObserver.observe(this.#footerElement);
      }
    }
  }

  #onIntersection(entries) {
    for (const entry of entries) {
      if (entry.target === this.#triggerElement) {
        // "Pasado de largo" y no simplemente fuera de pantalla: si el usuario está por encima del producto
        // (por ejemplo tras pulsar una miga de pan) la barra no tiene que aparecer.
        this.#triggerIsPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      } else if (entry.target === this.#footerElement) {
        this.#footerIsVisible = entry.isIntersecting;
      }
    }

    this.classList.toggle('is-visible', this.#triggerIsPassed && !this.#footerIsVisible);
  }

  #onVariantAdd(event) {
    if (event.target === this.#form) {
      this.#sheet?.hide();
    }
  }

  #onSheetToggle(event) {
    // "toggle" también es un evento del navegador (<details>, popover…), así que hay que comprobar de quién viene.
    if (event.target !== this.#sheet) {
      return;
    }

    if (event.target.open) {
      this.#prerenderOptions();
    } else if (this.#syncIsPending) {
      this.#syncIsPending = false;
      this.#syncToMainProduct();
    }
  }

  /**
   * Adelanta el HTML de las combinaciones que el usuario todavía no ha elegido, en cuanto la hoja se abre. Sin esto
   * cada toque en un color paga la petición entera antes de poder pintar nada: <variant-picker> ya adelanta ese
   * trabajo por su cuenta, pero solo cuando el selector entra en pantalla, y el de la barra está oculto en móvil, así
   * que ese momento no llega nunca.
   *
   * El "pointerenter" sobre la etiqueta es el mismo camino que recorre el ratón al pasar por encima en escritorio: no
   * cambia nada de la página, solo llena la caché del selector.
   */
  #prerenderOptions() {
    const form = this.#form;

    if (this.#optionsArePrerendered || !form) {
      return;
    }

    this.#optionsArePrerendered = true;

    const prerender = () => {
      for (const input of form.elements) {
        // Solo los valores de la hoja: los de la barra llevan el <input> dentro de su <label> y no encajan con el
        // selector que escucha <variant-picker> ("input + label").
        if (!input.matches?.('input[data-option-position]:not(:checked)')) {
          continue;
        }

        const label = input.nextElementSibling;

        if (label?.tagName === 'LABEL') {
          label.dispatchEvent(new PointerEvent('pointerenter'));
        }
      }
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(prerender, { timeout: 1000 });
    } else {
      prerender();
    }
  }

  /**
   * Replica en la ficha la combinación activa en la barra. Devuelve una promesa por si alguien quiere esperarla.
   */
  #syncToMainProduct() {
    const sourceForm = this.#form;
    const mainVariantPicker = this.#mainVariantPicker;
    const targetForm = document.forms[mainVariantPicker?.getAttribute('form-id')];

    if (!sourceForm || !targetForm) {
      return Promise.resolve();
    }

    const optionValues = activeOptionValues(sourceForm);

    if (optionValues.length === 0 || optionValues.join() === activeOptionValues(targetForm).join()) {
      return Promise.resolve();
    }

    return mainVariantPicker.selectCombination({ optionValues, productChange: false });
  }

  async #onVariantChange(event) {
    const sourceForm = event.target;

    if (!(sourceForm instanceof HTMLFormElement)) {
      return;
    }

    // form.id devuelve el <input name="id"> (los formularios exponen sus campos por nombre), no el atributo.
    const sourceFormId = sourceForm.getAttribute('id');
    const mainVariantPicker = this.#mainVariantPicker;

    if (!mainVariantPicker) {
      return;
    }

    if (sourceFormId === this.getAttribute('form')) {
      // Con la hoja abierta la ficha queda tapada de arriba abajo: rehacerla en cada toque es trabajo que nadie ve y
      // que además le disputa el ancho de banda a la foto de la variante, que sí se está mirando. Se apunta como
      // pendiente y se pone al día de una vez al cerrar.
      if (this.#sheet?.open) {
        this.#syncIsPending = true;
        return;
      }

      await this.#syncToMainProduct();
      return;
    }

    if (sourceFormId !== mainVariantPicker.getAttribute('form-id')) {
      // El cambio viene de otro producto (producto destacado, compra rápida…): no nos afecta.
      return;
    }

    const targetVariantPicker = this.#variantPicker;
    const targetForm = document.forms[targetVariantPicker?.getAttribute('form-id')];

    if (!targetForm) {
      return;
    }

    const optionValues = activeOptionValues(sourceForm);

    if (optionValues.length === 0 || optionValues.join() === activeOptionValues(targetForm).join()) {
      return;
    }

    await targetVariantPicker.selectCombination({ optionValues, productChange: false });
  }
}

if (!window.customElements.get('sticky-add-to-cart')) {
  window.customElements.define('sticky-add-to-cart', StickyAddToCart);
}
